import { useSyncExternalStore } from "react";

import type { Session as PbSession } from "@/gen/nodebase/v1/account_pb";

// Session is the browser's copy of an account login. It deliberately mirrors
// only what the console needs to make calls and name the caller; everything
// else about the account is fetched, never cached.
export type Session = {
  email: string;
  accountId: string;
  accessToken: string;
  refreshToken: string;
  // Seconds since epoch. protobuf-es models int64 as bigint, which does not
  // survive JSON.stringify — so it is narrowed to a number at the boundary
  // here rather than blowing up inside localStorage months later.
  accessExpiresUnix: number;
  // Set only by the Web registration flow. Account creation commits before
  // regional compute is requested, so the Workers route uses this durable bit
  // to finish (or offer a retry for) the first EnsureWorker after a reload.
  newAccount: boolean;
};

const STORAGE_KEY = "nodebase.dash.session";
const REMEMBERED_ACCOUNT_KEY = "nodebase.dash.last-account";

function rememberEmail(email: string): void {
  try {
    localStorage.setItem(
      REMEMBERED_ACCOUNT_KEY,
      email.trim().toLowerCase(),
    );
  } catch {
    // Private/locked-down storage must not block authentication itself.
  }
}

// The session lives outside React because the Connect interceptor needs the
// current token on every call and is not a component. React subscribes to this
// store rather than owning it (see useSession).
let current: Session | null = load();
const listeners = new Set<() => void>();
// Upgrade existing signed-in browsers before an eventual expiry clears the
// old combined record. Without this, only post-release logins are remembered.
if (current?.email) rememberEmail(current.email);

function load(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    // A corrupt or unreadable entry is indistinguishable from no session, and
    // treating it as "logged out" is the only recovery that works.
    return null;
  }
}

// Remembering the account is deliberately independent from authentication.
// Expiry and ordinary sign-out delete bearer credentials but leave this
// non-secret hint so reauthentication does not start with retyping identity.
export function getRememberedEmail(): string {
  try {
    return localStorage.getItem(REMEMBERED_ACCOUNT_KEY) ?? "";
  } catch {
    return "";
  }
}

export function forgetRememberedAccount(): void {
  try {
    localStorage.removeItem(REMEMBERED_ACCOUNT_KEY);
  } catch {
    // Same storage constraint as getRememberedEmail: forgetting is best effort.
  }
}

export function getSession(): Session | null {
  return current;
}

export function setSession(next: Session | null): void {
  current = next;
  if (next) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    rememberEmail(next.email);
    deleted = null;
    storeDeleted(null);
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  for (const l of listeners) l();
}

// A deleted account's address, kept for the one screen that says so. The
// session is gone the moment deletion succeeds, which on its own would drop the
// person on a login card with no word about what just happened. Tab-scoped
// storage, so a reload still shows the result but a new visit does not.
const DELETED_KEY = "nodebase.dash.account-deleted";
let deleted: string | null = loadDeleted();

function loadDeleted(): string | null {
  try {
    return sessionStorage.getItem(DELETED_KEY);
  } catch {
    return null;
  }
}

function storeDeleted(email: string | null): void {
  try {
    if (email) sessionStorage.setItem(DELETED_KEY, email);
    else sessionStorage.removeItem(DELETED_KEY);
  } catch {
    // The result screen is a courtesy; the deletion itself already happened.
  }
}

// endDeletedAccount signs this browser out of an account the server has just
// deleted. There is no Logout call: deletion revoked every session already.
// The remembered address goes too, since it now names nothing.
export function endDeletedAccount(email: string): void {
  deleted = email;
  storeDeleted(email);
  forgetRememberedAccount();
  setSession(null);
}

export function dismissDeletedAccount(): void {
  deleted = null;
  storeDeleted(null);
  for (const l of listeners) l();
}

export function useDeletedAccount(): string | null {
  return useSyncExternalStore(subscribe, () => deleted, () => null);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// useSession re-renders the tree when the token store changes — including when
// the transport's refresh path drops a dead session, which is not a React event
// but must still put the login card back on screen.
export function useSession(): Session | null {
  return useSyncExternalStore(subscribe, getSession, () => null);
}

// sessionFrom folds a login/refresh response into what we persist. The email is
// carried in because the server does not echo it back and the panels need it to
// state their scope.
export function sessionFrom(
  email: string,
  accountId: string,
  pb: PbSession | undefined,
  newAccount = false,
): Session {
  return {
    email,
    accountId,
    accessToken: pb?.accessToken ?? "",
    refreshToken: pb?.refreshToken ?? "",
    accessExpiresUnix: Number(pb?.accessExpiresUnix ?? 0n),
    newAccount,
  };
}
